import React, { useState, useEffect } from 'react';
import { Send, Calendar, Mail, Phone } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  useEffect(() => {
    // Force scroll to top immediately
    window.scrollTo(0, 0);
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    // Delayed scroll to handle any async content
    const timer = setTimeout(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 50);
    
    // Load Calendly widget script
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);
    
    return () => {
      clearTimeout(timer);
      // Cleanup script on unmount
      const existingScript = document.querySelector('script[src="https://assets.calendly.com/assets/external/widget.js"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitStatus('success');
      setFormData({ fullName: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="contact-page" data-testid="contact-page">
      <div className="contact-header">
        <h1>Get in Touch</h1>
        <p>
          Ready to start your recovery journey? Reach out today or schedule a 
          consultation to discuss how I can support you.
        </p>
      </div>

      <div className="contact-content">
        <div className="contact-form-section">
          <h2>Send a Message</h2>
          
          <form onSubmit={handleSubmit} data-testid="contact-form">
            <div className="form-group">
              <label htmlFor="fullName">Full Name *</label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                placeholder="John Doe"
                value={formData.fullName}
                onChange={handleChange}
                required
                data-testid="input-fullname"
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="john@example.com"
                value={formData.email}
                onChange={handleChange}
                required
                data-testid="input-email"
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone Number</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                placeholder="(555) 123-4567"
                value={formData.phone}
                onChange={handleChange}
                data-testid="input-phone"
              />
            </div>

            <div className="form-group">
              <label htmlFor="message">Message *</label>
              <textarea
                id="message"
                name="message"
                placeholder="Tell me about your recovery goals and how I can help..."
                value={formData.message}
                onChange={handleChange}
                required
                data-testid="input-message"
              />
            </div>

            <button 
              type="submit" 
              className="submit-btn" 
              disabled={isSubmitting}
              data-testid="submit-btn"
            >
              {isSubmitting ? 'Sending...' : 'Send Message'} <Send size={18} />
            </button>

            {submitStatus === 'success' && (
              <p style={{ color: '#10b981', marginTop: '15px', textAlign: 'center' }}>
                Thank you! Your message has been sent successfully.
              </p>
            )}
            {submitStatus === 'error' && (
              <p style={{ color: '#ef4444', marginTop: '15px', textAlign: 'center' }}>
                There was an error sending your message. Please try again.
              </p>
            )}
          </form>
        </div>

        <div className="contact-sidebar">
          <div className="schedule-card" data-testid="schedule-card">
            <h2>Schedule a <span>Consultation</span></h2>
            
            <p>
              Ready to take the next step? Schedule a free 20-minute consultation 
              to discuss your recovery goals and find the right coaching package for you.
            </p>
            
            {/* Calendly Inline Widget */}
            <div 
              className="calendly-inline-widget" 
              data-url="https://calendly.com/drea-drdreasobercoaching/free-consultation-w-dr-drea?hide_gdpr_banner=1&background_color=1c2128&text_color=ffffff&primary_color=d4a853"
              style={{ minWidth: '280px', height: '450px' }}
              data-testid="calendly-widget"
            />
          </div>

          <div className="contact-info-card" data-testid="contact-info-card">
            <h3>Contact Info</h3>
            <p><Mail size={18} color="#d4a853" /> drea@drdreasobercoaching.com</p>
            <p><Phone size={18} color="#d4a853" /> Available during business hours</p>
            <p style={{ marginLeft: '30px' }}>Mon-Fri: 9am - 6pm PST</p>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ContactPage;
